/**
 * Microcourse Studio — 前端主题注册
 * 从 shared constants 获取主题定义，提供 CSS 注入能力
 */

import { MICRO_THEMES, getTheme, getThemeCSS, DEFAULT_THEME_ID } from '@ai-tutor/shared'
import type { ThemeDefinition } from '@ai-tutor/shared'

export { MICRO_THEMES, getTheme, getThemeCSS, DEFAULT_THEME_ID }
export type { ThemeDefinition }

/**
 * 将主题 CSS 注入到 <style> 或 <link> 标签中（用于预览区域）
 * - 自定义主题（micro-*）：注入 <style> 内联 CSS
 * - 内置主题（black, white, ...）：加载 reveal.js CDN <link>
 */
export function injectThemeCSS(themeId: string, container?: HTMLElement): () => void {
  const theme = getTheme(themeId)
  if (!theme) return () => {}

  const target = container ?? document.head

  // 内置主题：从 CDN 加载 reveal.js 主题 CSS
  if (theme.type === 'builtin') {
    const link = document.createElement('link')
    link.setAttribute('rel', 'stylesheet')
    link.setAttribute('data-microcourse-theme', themeId)
    link.href = getRevealThemeUrl(themeId)
    target.appendChild(link)
    return () => link.remove()
  }

  // 自定义主题：注入内联 CSS
  const css = theme.css
  if (!css) return () => {}

  const style = document.createElement('style')
  style.setAttribute('data-microcourse-theme', themeId)
  style.textContent = css
  target.appendChild(style)

  return () => {
    style.remove()
  }
}

/**
 * 获取主题的 reveal.js CDN 路径（内置主题）
 */
export function getRevealThemeUrl(themeId: string): string {
  const theme = getTheme(themeId)
  if (!theme || theme.type === 'custom') {
    // 自定义主题使用 black 作为 base
    return 'https://cdn.jsdelivr.net/npm/reveal.js@4.6.1/dist/theme/black.css'
  }
  return `https://cdn.jsdelivr.net/npm/reveal.js@4.6.1/dist/theme/${themeId}.css`
}
